import { useEffect, useState } from 'react';
import { Container, Form, Button, Alert, Row, Col, Card } from 'react-bootstrap';
import { FiHelpCircle, FiMail, FiSend, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import { useOrgContext } from '../../context/org';
import { useAuth } from '../../context/auth';
import { useMutation } from 'react-query';
import { contactSupport } from '../../api/auth';
import SpinnerFullSection from '../Other/SpinnerFullSection';
import { Link } from 'react-router-dom';

const Support = () => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [org, setOrg] = useState("");
    const [agent, setAgent] = useState("");
    const [problemType, setProblemType] = useState("");
    const [message, setMessage] = useState("");

    const {orgs} = useOrgContext();
    const {user} = useAuth();
    const problemTypes = ['General', 'Billing', 'Technical', 'Account', 'Organization'];

    const {
        mutateAsync: contactSupportFn, 
        isLoading, 
        isSuccess,
        isError
    } = useMutation(contactSupport, {
        onSuccess: () => {},
        onError: (error) => {}
    })

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        contactSupportFn({
            name,
            email,
            org,
            agent,
            problemType,
            message
        })
    };

    useEffect(()=>{
        if(user) {
            setName(`${user.firstName} ${user.lastName}`);
            setEmail(user.email);
        }
    }, [user])

    return (
        <Container style={{maxWidth:800, margin:"0 auto"}} className='my-5'>

            {isLoading && <SpinnerFullSection />}

            <div className="d-flex align-items-center mb-4">
                <FiHelpCircle size={32} className="text-primary me-3" />
                <h2 className="text-white mb-0">Help & Support</h2>
            </div>

            <Alert variant="info" className="bg-info bg-opacity-25 text-light border-info mb-4">
                <div className="d-flex align-items-start">
                    <FiHelpCircle className="text-info me-3 mt-1" size={20} />
                    <div>
                        <strong>Quick Help Available</strong>
                        <p className="mb-0 mt-1">
                            Before submitting a ticket, try our AI chatbot using the floating chat button. 
                            It may quickly resolve your issue.
                        </p>
                    </div>
                </div>
            </Alert>

            <Card className="bg-dark border-secondary">
                <Card.Header className="bg-dark border-secondary">
                    <div className="d-flex align-items-center">
                        <FiMail size={20} className="text-primary me-2" />
                        <h4 className="text-white mb-0">Submit Support Ticket</h4>
                    </div>
                </Card.Header>
                <Card.Body className="bg-dark">
                    <p className="text-light mb-4">
                        If you need further assistance, please submit a support ticket below, or email us at{' '}
                        <Link to="mailto:support@example.com" className='text-primary'>support@example.com</Link>.
                    </p>
                    
                    { isSuccess && 
                        <Alert variant="success" className="bg-success bg-opacity-25 text-light border-success mb-4">
                            <div className="d-flex align-items-start">
                                <FiCheckCircle className="text-success me-3 mt-1" size={20} />
                                <div>
                                    <h5 className="text-success mb-2">Request Submitted Successfully!</h5>
                                    <p className="mb-0">
                                        Our team will reach out to you soon via your email.
                                        All communications regarding this request will be conducted through email.
                                    </p>
                                </div>
                            </div>
                        </Alert>
                    }

                    {       isError &&
                        <Alert variant="danger" className="bg-danger bg-opacity-25 text-light border-danger mb-4">
                            <div className="d-flex align-items-start">
                                <FiAlertTriangle className="text-danger me-3 mt-1" size={20} />
                                <div>
                                    <h5 className="text-danger mb-2">Failed to Submit Request</h5>
                                    <p className="mb-0">
                                        Please try again later or contact us directly via{' '}
                                        <Link to="mailto:info@quickychat.com" className="text-primary">info@quickychat.com</Link>.
                                    </p>
                                </div>
                            </div>
                        </Alert>
                    }
                
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={6}>
                                <Form.Group controlId="formName" className="mb-3">
                                    <Form.Label className="text-white">Name*</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        placeholder="Enter your name"
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        className="bg-dark text-white border-secondary"
                                        style={{
                                            backgroundColor: '#212529',
                                            borderColor: '#6c757d',
                                            color: '#ffffff'
                                        }}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="formEmail" className="mb-3">
                                    <Form.Label className="text-white">Email*</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        placeholder="Enter your email"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        className="bg-dark text-white border-secondary"
                                        style={{
                                            backgroundColor: '#212529',
                                            borderColor: '#6c757d',
                                            color: '#ffffff'
                                        }}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group controlId="formOrganization" className="mb-3">
                                    <Form.Label className="text-white">Select Organization*</Form.Label>
                                    <Form.Control
                                        as="select"
                                        name="organization"
                                        className="bg-dark text-white border-secondary"
                                        value={org}
                                        onChange={e => {setOrg(e.target.value);}}
                                        style={{
                                            backgroundColor: '#212529',
                                            borderColor: '#6c757d',
                                            color: '#ffffff'
                                        }}
                                        required
                                    >
                                        <option value="">Choose an organization</option>
                                        {orgs?.map((org, index) => (
                                            <option key={index} value={org.name}>{org.name}</option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="formAgent" className="mb-3">
                                    <Form.Label className="text-white">Agent</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="agent"
                                        placeholder="Agent name (optional)"
                                        value={agent}
                                        onChange={e => setAgent(e.target.value)}
                                        className="bg-dark text-white border-secondary"
                                        style={{
                                            backgroundColor: '#212529',
                                            borderColor: '#6c757d',
                                            color: '#ffffff'
                                        }}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Form.Group controlId="formProblemType" className="mb-3">
                            <Form.Label className="text-white">Support Category*</Form.Label>
                            <Form.Control
                                as="select"
                                name="problemType"
                                className="bg-dark text-white border-secondary"
                                value={problemType}
                                onChange={e => setProblemType(e.target.value)}
                                style={{
                                    backgroundColor: '#212529',
                                    borderColor: '#6c757d',
                                    color: '#ffffff'
                                }}
                                required
                            >
                                <option value="">Select category</option>
                                {problemTypes.map((type, index) => (
                                    <option key={index} value={type}>{type}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>

                        <Form.Group controlId="formMessage" className="mb-4">
                            <Form.Label className="text-white">Message*</Form.Label>
                            <Form.Control
                                as="textarea"
                                name="message"
                                placeholder="Describe your issue in detail..."
                                rows={4}
                                value={message}
                                onChange={e => setMessage(e.target.value)}
                                className="bg-dark text-white border-secondary"
                                style={{
                                    backgroundColor: '#212529',
                                    borderColor: '#6c757d',
                                    color: '#ffffff',
                                    resize: 'vertical'
                                }}
                                required
                            />
                        </Form.Group>

                        <div className="d-flex justify-content-end">
                            <Button 
                                variant="primary" 
                                type="submit"
                                className="d-flex align-items-center gap-2 px-4"
                                disabled={!name || !email || !org || !problemType || !message}
                            >
                                <FiSend size={16} />
                                Submit Ticket
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default Support;
